<template>
  <v-container fluid class="pa-4">
    <v-row justify="space-between" align="center" class="mb-4">
      <v-col cols="12" md="8">
        <h1 class="text-h4 font-weight-bold primary--text">
          <v-icon large color="primary" class="mr-3">mdi-calculator-variant</v-icon>
          Control de handicaps
        </h1>
        <div class="grey--text text--darken-1">
          Captura, validación, cálculo y publicación del índice de cada socio.
        </div>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn color="success" rounded outlined class="mr-2" @click="batchImportDialog = true">
          <v-icon left small>mdi-file-excel</v-icon>
          Importar
        </v-btn>
        <v-btn color="primary" rounded class="mr-2" @click="openCreatePlayer">
          <v-icon left small>mdi-account-plus</v-icon>
          Nuevo golfista
        </v-btn>
        <v-btn color="primary" rounded outlined :loading="loading" @click="loadData">
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dismissible @input="clearError">
      {{ error }}
    </v-alert>
    <v-alert v-if="importSuccess" type="success" dismissible @input="importSuccess = ''">
      {{ importSuccess }}
    </v-alert>

    <v-alert v-if="configurationIssues" type="warning" outlined>
      Hay {{ configurationIssues }} salida(s) con configuración incompleta de tees, rating, slope o hoyos.
      Revísalas antes de validar nuevas rondas.
    </v-alert>

    <v-row class="mb-2">
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text>
            <div class="text-caption grey--text">SOCIOS</div>
            <div class="text-h4 font-weight-bold">{{ players.length }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text>
            <div class="text-caption grey--text">PROMEDIO PUBLICADO</div>
            <div class="text-h4 font-weight-bold">{{ clubAverage }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text>
            <div class="text-caption grey--text">MEJOR ÍNDICE</div>
            <div class="text-h4 font-weight-bold">{{ bestHandicap }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="elevation-2">
      <v-card-title>
        Socios golfistas
        <v-spacer></v-spacer>
        <v-chip color="primary" small text-color="white">{{ players.length }}</v-chip>
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Nombre o número de socio"
              outlined dense clearable hide-details
              @keyup.enter="loadPlayers"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Estado"
              outlined dense clearable hide-details
              @change="loadPlayers"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="authorityFilter"
              :items="authorityOptions"
              label="Autoridad"
              outlined dense clearable hide-details
              @change="loadPlayers"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-data-table
        :headers="headers"
        :items="players"
        :loading="loading"
        no-data-text="No hay perfiles que coincidan con los filtros"
        class="handicap-table"
        @click:row="selectPlayer"
      >
        <template v-slot:item.current_handicap_index="{ item }">
          <v-chip :color="item.current_handicap_index === null ? 'grey' : 'primary'" dark small>
            {{ item.current_handicap_index === null ? 'NH' : item.current_handicap_index }}
          </v-chip>
        </template>
        <template v-slot:item.handicap_authority="{ item }">
          {{ item.handicap_authority === 'ghin' ? 'GHIN' : 'Local' }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="item.status === 'active' ? 'success' : 'grey'" outlined x-small>
            {{ item.status === 'active' ? 'Activo' : 'Inactivo' }}
          </v-chip>
        </template>
        <template v-slot:item.updated_at="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>
      </v-data-table>
    </v-card>

    <HandicapDetailDialog
      :visible="showDetail"
      :player="selectedPlayer"
      @close="closeDetail"
      @updated="loadPlayers"
    />
    <HandicapBatchImportDialog
      :visible="batchImportDialog"
      @close="batchImportDialog = false"
      @imported="batchImported"
    />

    <v-dialog v-model="createPlayerDialog" max-width="620px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon dark class="mr-2">mdi-account-plus</v-icon>
          Alta de golfista
        </v-card-title>
        <v-card-text class="pa-6">
          <v-alert type="info" dense outlined>
            Crea el expediente de handicap para capturar tarjetas aunque el socio todavía no haya registrado una puntuación.
          </v-alert>
          <v-form ref="createPlayerForm" v-model="createPlayerValid">
            <v-autocomplete
              v-model="selectedIdentityKey"
              :search-input.sync="identitySearch"
              :items="availableIdentityOptions"
              :item-text="identityOptionLabel"
              item-value="key"
              label="Vincular con socio de la plataforma"
              outlined dense clearable
              :loading="identityLoading"
              hint="Recomendado para que las tarjetas de la app usen este mismo expediente"
              persistent-hint
              @change="applySelectedIdentity"
            />
            <v-text-field
              v-model="newPlayer.display_name"
              label="Nombre completo"
              outlined dense
              :rules="requiredRules"
              hint="Como aparecerá en listas y tarjetas"
              persistent-hint
            />
            <v-text-field
              v-model="newPlayer.membership_number"
              label="Clave o número de socio"
              outlined dense
              counter="64"
              :rules="membershipRules"
              hint="Debe ser único dentro del club"
              persistent-hint
            />
            <v-row>
              <v-col cols="12" sm="6">
                <v-select v-model="newPlayer.gender" :items="genderOptions" label="Categoría" outlined dense />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="newPlayer.status" :items="playerStatusOptions" label="Estado" outlined dense />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createPlayerDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!createPlayerValid" :loading="operationLoading" @click="savePlayer">
            Crear expediente
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { handicapAPI } from '@/services/handicap'
import HandicapBatchImportDialog from './components/HandicapBatchImportDialog.vue'
import HandicapDetailDialog from './components/HandicapDetailDialog.vue'

export default {
  name: 'GolfHandicaps',
  components: { HandicapBatchImportDialog, HandicapDetailDialog },
  data () {
    return {
      search: '',
      statusFilter: null,
      authorityFilter: null,
      showDetail: false,
      selectedPlayer: null,
      batchImportDialog: false,
      importSuccess: '',
      createPlayerDialog: false,
      createPlayerValid: false,
      newPlayer: { display_name: '', membership_number: '', gender: 'unspecified', status: 'active', user: null, ccslp_user: null },
      identityOptions: [],
      identityLoading: false,
      identitySearch: '',
      identitySearchTimer: null,
      selectedIdentityKey: null,
      requiredRules: [value => Boolean(value?.trim()) || 'Campo requerido'],
      membershipRules: [value => !value || value.length <= 64 || 'Máximo 64 caracteres'],
      genderOptions: [
        { text: 'Caballeros', value: 'male' },
        { text: 'Damas', value: 'female' },
        { text: 'Sin especificar', value: 'unspecified' }
      ],
      playerStatusOptions: [
        { text: 'Activo', value: 'active' },
        { text: 'Inactivo', value: 'inactive' }
      ],
      statusOptions: [
        { text: 'Activos', value: 'active' },
        { text: 'Inactivos', value: 'inactive' }
      ],
      authorityOptions: [
        { text: 'Cálculo local', value: 'local' },
        { text: 'GHIN', value: 'ghin' }
      ],
      headers: [
        { text: 'Socio', value: 'display_name' },
        { text: 'Número', value: 'membership_number' },
        { text: 'Índice', value: 'current_handicap_index' },
        { text: 'Autoridad', value: 'handicap_authority' },
        { text: 'Estado', value: 'status' },
        { text: 'Actualizado', value: 'updated_at' }
      ]
    }
  },
  computed: {
    ...mapState('handicap', ['players', 'configuration', 'loading', 'operationLoading', 'error']),
    ...mapGetters('handicap', ['clubAverageHandicap', 'bestHandicapInClub']),
    clubAverage () {
      return this.clubAverageHandicap
    },
    bestHandicap () {
      return this.bestHandicapInClub?.current_handicap_index ?? '--'
    },
    configurationIssues () {
      return this.configuration.filter(course => course.ready === false).length
    },
    availableIdentityOptions () {
      return this.identityOptions.filter(option => !option.linked_profile_id)
    }
  },
  mounted () {
    this.loadData()
  },
  beforeDestroy () {
    if (this.identitySearchTimer) clearTimeout(this.identitySearchTimer)
  },
  watch: {
    identitySearch (value) {
      if (!this.createPlayerDialog) return
      if (this.identitySearchTimer) clearTimeout(this.identitySearchTimer)
      this.identitySearchTimer = setTimeout(() => this.loadIdentityOptions(value), 250)
    }
  },
  methods: {
    ...mapActions('handicap', ['fetchAllHandicaps', 'fetchConfiguration', 'createPlayerProfile', 'clearOverview', 'clearError']),
    async loadData () {
      await Promise.all([this.loadPlayers(), this.fetchConfiguration()])
    },
    loadPlayers () {
      const params = {}
      if (this.search?.trim()) params.search = this.search.trim()
      if (this.statusFilter) params.status = this.statusFilter
      if (this.authorityFilter) params.authority = this.authorityFilter
      return this.fetchAllHandicaps(params)
    },
    selectPlayer (player) {
      this.selectedPlayer = player
      this.showDetail = true
    },
    async openCreatePlayer () {
      this.newPlayer = { display_name: '', membership_number: '', gender: 'unspecified', status: 'active', user: null, ccslp_user: null }
      this.selectedIdentityKey = null
      this.identitySearch = ''
      this.createPlayerDialog = true
      await this.loadIdentityOptions()
      this.$nextTick(() => this.$refs.createPlayerForm?.resetValidation())
    },
    async loadIdentityOptions (search = '') {
      this.identityLoading = true
      try {
        const params = search?.trim() ? { search: search.trim() } : {}
        const response = await handicapAPI.listIdentityOptions(params)
        this.identityOptions = response.data?.results || response.data || []
      } finally {
        this.identityLoading = false
      }
    },
    identityOptionLabel (option) {
      const number = option.membership_number ? ` · ${option.membership_number}` : ''
      return `${option.display_name}${number}`
    },
    applySelectedIdentity (key) {
      const option = this.identityOptions.find(item => item.key === key)
      this.newPlayer.user = option?.type === 'user' ? option.id : null
      this.newPlayer.ccslp_user = option?.type === 'ccslp_user' ? option.id : null
      if (!option) return
      this.newPlayer.display_name = option.display_name
      if (option.membership_number) this.newPlayer.membership_number = option.membership_number
    },
    async savePlayer () {
      if (!this.$refs.createPlayerForm.validate()) return
      try {
        const player = await this.createPlayerProfile({
          ...this.newPlayer,
          display_name: this.newPlayer.display_name.trim(),
          membership_number: this.newPlayer.membership_number.trim()
        })
        this.createPlayerDialog = false
        this.selectPlayer(player)
      } catch (error) {
        // El store expone el mensaje de la API en la alerta principal.
      }
    },
    async batchImported (result) {
      this.batchImportDialog = false
      this.importSuccess = `Se crearon ${result.created} ronda(s) en borrador para revisión.`
      await this.loadPlayers()
    },
    closeDetail () {
      this.showDetail = false
      this.selectedPlayer = null
      this.clearOverview()
    },
    formatDate (date) {
      if (!date) return '--'
      return new Date(date).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })
    }
  }
}
</script>

<style scoped>
.handicap-table >>> tbody tr { cursor: pointer; }
</style>
